import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PILOT_URL } from "./Header";

const PLANS = [
  {
    name: "Core",
    line: "$1,500/site/month + $5,000 setup. Includes WSafe AI, HazardLoop, WorkSafeStory.",
  },
  {
    name: "Pro",
    line: "$3,000/site/month + $10,000 setup. Includes everything in Core plus InductIQ and EnviSafe.",
  },
  {
    name: "Vision",
    line: "$6,250/site/month + $25,000 setup. Includes everything in Pro plus SafeForm and continuous CCTV detection.",
  },
];

export const Pricing = () => {
  return (
    <section id="pricing" data-testid="pricing-section" className="bg-white py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-safety">Pricing</p>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Straightforward, per-site pricing
          </h2>
          <p data-testid="pricing-subscriptions-note" className="mt-4 text-base text-slate-600">
            Subscriptions, per site per month, plus one-time setup per site:
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-3">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="flex flex-col rounded-xl border border-slate-200 bg-slate-50 p-8 shadow-sm transition-shadow hover:shadow-md"
            >
              <h3 className="text-xl font-semibold text-navy sm:text-2xl">{plan.name}</h3>
              <p className="mt-4 text-base leading-relaxed text-slate-700">{plan.line}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          data-testid="pricing-pilot-banner"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          className="mt-10 flex flex-col items-start justify-between gap-6 rounded-xl border-2 border-safety bg-safety-light p-8 sm:flex-row sm:items-center"
        >
          <div>
            <h3 className="text-xl font-semibold text-navy">Founding paid pilot</h3>
            <p className="mt-2 text-base text-slate-700">$1,000, no install fee.</p>
          </div>
          <a
            href={PILOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pricing-pilot-button"
            className="inline-flex items-center gap-2 rounded-full bg-safety px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-safety-dark"
          >
            Request the $1,000 founding paid pilot
            <ArrowRight className="h-4 w-4" />
          </a>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2">
          <div
            data-testid="pricing-compliance-block"
            className="rounded-xl border border-slate-200 bg-white p-8"
          >
            <h3 className="text-lg font-semibold text-navy">
              Compliance Evaluation &amp; Documentation Service
            </h3>
            <p className="mt-3 text-base leading-relaxed text-slate-600">
              $1,500 evaluation; then Essentials $500, Standard $1,000 or Premium $2,000 per
              site/month, billed in addition to a Core or Pro subscription. Covers OSHA and
              environmental records. Documentation setup is included in the setup fee; work beyond
              included hours is $150/hr.
            </p>
          </div>
          <div
            data-testid="pricing-consulting-block"
            className="rounded-xl border border-slate-200 bg-white p-8"
          >
            <h3 className="text-lg font-semibold text-navy">Consulting</h3>
            <p className="mt-3 text-base leading-relaxed text-slate-600">
              $150/hr; $1,500 gap review; $1,500/day audit; $2,500 AI readiness assessment (credited
              toward a paid pilot); $1,200/month retainer. No travel charge for on-site work within
              50 miles of Abilene, Texas.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
